import { resumeBuilderApis } from '@/api/resume-builder';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { IThunkAPI, IWorkHistory } from '../types';
import type {
  IApplicationPayload,
  IContact,
  IProfilePicturePayload,
  IResumeBuilderInitialState,
  IStepStatus,
  ITestStatusPayLoad,
} from '@/types/resume-builder.model';
import { activeStepIdToName } from '@/types/resume-builder.model';

export const initialState: Readonly<IResumeBuilderInitialState> = Object.freeze({
  activeStep: 1,
  showSuccessModal: false,
  activeStepName: 'contact',
  contact: undefined,
  internetTestHistory: [],
  testStatus: [],
  workhistory: [],
  profilePicture: undefined,
  hardwareList: [],
  hardwareTestToken: '',
  hardwareDetail: {
    userId: 0,
    hardwareTestId: 0,
    timestampTest: 0,
    testStatus: 0,
    resultBitmask: 0,
    json: { system: undefined },
  },
  voiceQuestionList: [],
  voiceAnswers: [],
  applicationFlags: {
    applicationId: 0,
    campaignId: 0,
    userId: 0,
    agentContractId: 0,
    applicationStatusId: 0,
    createdTimestamp: 0,
    createdDatetime: '',
    hasWorkHistory: false,
    ackBaseHours: false,
    ackPeakHours: false,
    ackShippingAddress: false,
    ackFailedShipmentFee: false,
    ackTax: false,
    ackBackgroundCheck: false,
    countryId: 0,
    modifiedTimestamp: '',
    modifiedDatetime: new Date().toISOString(),
  },
  applicationFlagsLoading: false,
});

// set active step status
export const setStepStatus = createAsyncThunk<unknown, IStepStatus, IThunkAPI>(
  'jobapplication/setStatus',
  async (stepStatus, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.setStepStatus(stepStatus);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setApplicationFlags = createAsyncThunk<any, IApplicationPayload, IThunkAPI>(
  'jobapplication/setApplication',
  async (applicationPayload, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.setApplicationFlags(applicationPayload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getApplicationFlags = createAsyncThunk<unknown, number, IThunkAPI>(
  'jobapplication/getApplicationFlags',
  async (applicationId, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getApplicationFlags(applicationId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

//get internet test history
export const getInternetTestHistory = createAsyncThunk<unknown, undefined | number, IThunkAPI>(
  'speedtest/list',
  async (userId, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getInternetTestHistory(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

//get internet test history
export const getApplicationInternetTest = createAsyncThunk<unknown, number, IThunkAPI>(
  'application/speedtest',
  async (applicationId, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getApplicationInternetTest(applicationId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

//get internet test status
export const getTestStatus = createAsyncThunk<unknown, ITestStatusPayLoad, IThunkAPI>(
  'test/status',
  async (testStatus, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getTestStatus(testStatus.requirementType, testStatus.userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// get active step
export const getStepStatus = createAsyncThunk<unknown, number, IThunkAPI>(
  'jobapplication/getStatus',
  async (jobId, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getStepStatus(jobId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// get contact information
export const getContactInformation = createAsyncThunk<unknown, undefined, IThunkAPI>(
  'member/get/profile',
  async (_, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getContactInformation();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setContactInformation = createAsyncThunk<unknown, IContact, IThunkAPI>(
  'member/set/profile',
  async (payload, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.setContactInformation(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// set profile picture
export const setProfilePicture = createAsyncThunk<unknown, IProfilePicturePayload, IThunkAPI>(
  'member/profile/photo',
  async (photoPayload, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.setProfilePicture(photoPayload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// get profile picture
export const getProfilePicture = createAsyncThunk<unknown, File | undefined, IThunkAPI>(
  'member/profile/photo/get',
  async (_, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getProfilePicture();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// add work experience
export const setWorkHistory = createAsyncThunk<unknown, IWorkHistory, IThunkAPI>(
  'workhistory/add',
  async (workhistory, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.setWorkHistory(workhistory);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// delete work exp
export const deleteWorkHistory = createAsyncThunk<
  unknown,
  {
    workHistoryId: number;
    workhistory: IWorkHistory[];
  },
  IThunkAPI
>('workhistory/delete', async ({ workHistoryId, workhistory }, thunkAPI) => {
  try {
    await resumeBuilderApis.deleteWorkHistory(workHistoryId);
    return workhistory.filter((work: IWorkHistory) => work.workHistoryId !== workHistoryId);
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

// update work exp
export const updateWorkHistory = createAsyncThunk<unknown, IWorkHistory, IThunkAPI>(
  'workhistory/edit',
  async (workHistory, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.updateWorkHistory(workHistory);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

// get work exp list
export const getWorkHistoryList = createAsyncThunk<unknown, undefined, IThunkAPI>(
  'workhistory/list',
  async (_, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getWorkHistoryList();

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getHardwareList = createAsyncThunk<unknown, undefined | number, IThunkAPI>(
  'hardware/list',
  async (userId, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getHardwareList(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getHardwareToken = createAsyncThunk<string, void, IThunkAPI>('hardwaretest/token', async (_, thunkAPI) => {
  try {
    const response = await resumeBuilderApis.getHardwareTestToken();
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getHardwareDetail = createAsyncThunk<unknown, number, IThunkAPI>(
  'hardware/detail',
  async (testid, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getHardwareDetail(testid);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVoiceQuestionList = createAsyncThunk<unknown, number, IThunkAPI>(
  'voicequestion/list',
  async (gigid, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getVoiceQuestionList(gigid);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);
export const getVoiceAnswers = createAsyncThunk<unknown, number, IThunkAPI>(
  'voiceanswer/list',
  async (applicationId, thunkAPI) => {
    try {
      const response = await resumeBuilderApis.getVoiceAnswers(applicationId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);
export const setVoiceAnswer = createAsyncThunk<
  unknown,
  { file: File | undefined; voiceQuestionId: number; applicationId: number },
  IThunkAPI
>('voicequestion/set', async (fileObj, thunkAPI) => {
  try {
    const { file, voiceQuestionId, applicationId } = fileObj;
    const response = await resumeBuilderApis.setVoiceAnswer(file, applicationId, voiceQuestionId);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const resumeBuilderSlice = createSlice({
  name: 'resumeBuilderSlice',
  initialState,
  reducers: {
    setActiveStep(state: IResumeBuilderInitialState, action: PayloadAction<number>) {
      state.activeStep = action.payload;
      state.activeStepName = activeStepIdToName[action.payload];
    },
    setSuccessModal(state: IResumeBuilderInitialState, action: PayloadAction<boolean>) {
      state.showSuccessModal = action.payload;
    },
  },
  extraReducers: {
    'jobapplication/getStatus/fulfilled': (state, action) => {
      const stepCount = action.payload
        ? action.payload?.filter((step: IStepStatus) => step?.vocabApplicationStepStatus?.label === 'Complete').length +
          1
        : state.activeStep;
      state.activeStep = stepCount;
      state.activeStepName = activeStepIdToName[stepCount];
    },
    'member/get/profile/fulfilled': (state, action) => {
      state.contact = action.payload;
    },
    'speedtest/list/fulfilled': (state, action) => {
      state.internetTestHistory = action.payload;
    },
    'jobapplication/getApplicationFlags/pending': state => {
      state.applicationFlagsLoading = true;
    },
    'jobapplication/getApplicationFlags/failed': state => {
      state.applicationFlagsLoading = false;
    },
    'jobapplication/getApplicationFlags/fulfilled': (state, action) => {
      state.applicationFlagsLoading = false;
      state.applicationFlags = action.payload;
    },
    'workhistory/list/fulfilled': (state, action) => {
      state.workhistory = action.payload;
    },
    'workhistory/delete/fulfilled': (state, action) => {
      state.workhistory = action.payload;
    },
    'member/profile/photo/get/fulfilled': (state, action) => {
      state.profilePicture = action.payload[0];
    },
    'test/status/fulfilled': (state, action) => {
      state.testStatus = action.payload;
    },
    'hardware/list/fulfilled': (state, action) => {
      state.hardwareList = action.payload;
    },
    'hardwaretest/token/fulfilled': (state, action) => {
      state.hardwareTestToken = action.payload;
    },
    'hardware/detail/fulfilled': (state, action) => {
      state.hardwareDetail = action.payload;
      state.hardwareDetail.json = JSON.parse(action.payload.json || '');
    },
    'voicequestion/list/fulfilled': (state, action) => {
      state.voiceQuestionList = action.payload;
    },
    'voiceanswer/list/fulfilled': (state, action) => {
      state.voiceAnswers = typeof action.payload === 'string' ? [] : action.payload;
    },
  },
});

export const { setActiveStep, setSuccessModal } = resumeBuilderSlice.actions;
export default resumeBuilderSlice.reducer;
